/**
 * Domain types for Create Chat functionality
 * These types are independent of GraphQL and represent the business domain
 */

import type { CutoffTime, CutoffType, Day } from './orderPreferences';

// Base types for select options
export interface BaseSelectOption {
  value: string;
  label: string;
}

// Chat type options (rename to avoid conflict with existing ChatType)
export enum CreateChatType {
  ORDERING = 'ordering',
  SUPPORT = 'support',
}

export interface ChatTypeOption extends BaseSelectOption {
  value: CreateChatType;
}

// Created context options
export enum CreatedContext {
  ADMIN = 'chatCreatedFromAdmin',
  BUYER_CONCIERGE = 'chatCreatedFromBuyerConcierge',
  CHOCO_AI = 'chatCreatedFromChocoAi',
  ADD_ANOTHER_BUSINESS = 'addAnotherBusiness',
  BUYER_ADD_SUPPLIER = 'buyerAddSupplier',
  BUYER_ADDS_VERIFIED_SUPPLIER_NEW_CUSTOMER = 'buyerAddsVerifiedSupplierNewCustomer',
  BUYER_REQUESTS_SUPPLIER_EXISTING_CUSTOMER = 'buyerRequestsSupplierExistingCustomer',
  BUYER_REQUESTS_SUPPLIER_NEW_CUSTOMER = 'buyerRequestsSupplierNewCustomer',
  CUSTOMER_MASS_ACCOUNT_CREATOR = 'customerMassAccountCreator',
  INVITE_LINK = 'inviteLink',
  SUPPLIER_INVITES_BUYER = 'supplierInvitesBuyer',
}

export interface CreatedContextOption extends BaseSelectOption {
  value: CreatedContext;
}

// Order preference types
export enum OrderPreferenceType {
  WHATSAPP = 'WHATSAPP',
  SMS = 'SMS',
  FAX = 'FAX',
  EMAIL = 'EMAIL',
}

export interface OrderPreference {
  type: OrderPreferenceType;
  value: string;
}

// User metadata types
export interface UserMetadata {
  name?: string | null;
  supplierId?: string | null;
  phone?: string | null;
  email?: string | null;
  orderPreferences?: OrderPreference[];
  customerNumberMandatory?: boolean | null;
  supplier?: boolean;
  referenceId?: string | null;
  /**
   * The distributor's bot user id, carried on the distributor option so the
   * create form can address the distributor's primary notification recipient
   * before the chat exists (the bot user always leads the chat's userIds).
   */
  botUserId?: string | null;
  /**
   * Whether the distributor supports the eCommerce PO-number requirement.
   * When false/absent, the create form hides the PO-number control (the
   * capability must be enabled in the distributor's settings first).
   */
  allowEcommercePoNumber?: boolean | null;
}

export interface BuyerMetadata {
  fullAddress: string;
  name?: string | null;
  referenceId?: string | null;
}

// Select option types with metadata
export interface UserSelectOption extends BaseSelectOption {
  metaData?: UserMetadata;
}

export interface BuyerSelectOption extends BaseSelectOption {
  metaData?: BuyerMetadata;
}

export interface SupplierUserSelectOption extends BaseSelectOption {
  metaData?: UserMetadata & {
    supplier: true;
  };
}

export interface BuyerUserSelectOption extends BaseSelectOption {
  metaData?: UserMetadata & {
    supplier: false;
  };
}

// Delivery settings types
export enum CutOffTimeType {
  NO_CUTOFF = 'NoCutoff',
  SAME_DAY = 'SameDay',
  NEXT_DAY = 'NextDay',
  CUSTOM = 'Custom',
}

export interface CutOffTimeSetting {
  type: CutOffTimeType;
  time?: string; // Format: "HH:mm"
}

export interface WeekdayBasedCutoffSettings {
  mon: CutOffTimeSetting;
  tue: CutOffTimeSetting;
  wed: CutOffTimeSetting;
  thu: CutOffTimeSetting;
  fri: CutOffTimeSetting;
  sat: CutOffTimeSetting;
  sun: CutOffTimeSetting;
}

export interface DeliveryDay {
  day: string;
  enabled: boolean;
  cutoffSetting: CutOffTimeSetting;
}

// Sub-catalog types
export interface SubCatalogOption extends BaseSelectOption {
  supplierId: string;
  description?: string;
}

// Main form data types
export interface ChatParticipantsFormData {
  buyer: BuyerSelectOption | null;
  supplier: UserSelectOption | null;
  buyerUsers: BuyerUserSelectOption[];
  supplierUsers: SupplierUserSelectOption[];
  deselectedOrderPreferences: string[];
}

export interface ChatSettingsFormData {
  chatName: string;
  restaurantName: string;
  deliveryAddress: string;
  customerNumber: string;
  additionalEmails: string;
  chatType: ChatTypeOption | null;
  subCatalog: SubCatalogOption[];
  createdContext: CreatedContextOption | null;
  customerLevelMovAmount?: string | null;
  customerLevelMovCurrency?: string | null;
  visibleChatPrices?: boolean;
  isPriceOverrideEnabled: boolean;
  /**
   * Whether the eCommerce PO number is required for this chat. Only meaningful
   * when the distributor supports it (`metaData.allowEcommercePoNumber`); the
   * control is hidden otherwise. Applied post-create via updateChatOrderingSettings.
   * Optional because the Edit-chat settings form does not manage it (create-only,
   * #125).
   */
  isEcommercePoNumberRequired?: boolean;
}

/**
 * Create-time delivery settings. These mirror the edit-side delivery modal's
 * in-memory shape (per-day booleans/times/lead-times keyed by `Day`) so the
 * create flow can apply them through the SAME explicit mutation contract
 * (`updateSupplierDeliverySetting`, chat scope) that the modal uses.
 */
export interface ChatDeliverySettingsFormData {
  hasCustomChatDeliverySettings: boolean;
  deliveryDays: Record<Day, boolean>;
  cutoffType: CutoffType;
  cutoffTimes: Record<Day, CutoffTime>;
  cutoffDays: Record<Day, string>;
}

// Combined form data
export interface CreateChatFormData
  extends
    ChatParticipantsFormData,
    ChatSettingsFormData,
    ChatDeliverySettingsFormData {}

// API input types (what gets sent to the server)
export interface CreateChatInput {
  chatName?: string | null;
  restaurantName?: string | null;
  deliveryAddress?: string | null;
  customerNumber?: string | null;
  additionalEmails?: string[];
  type: CreateChatType;
  supplierId: string;
  buyerId?: string;
  userIds: string[];
  subCatalogId?: string | null;
  isPuppy?: boolean;
  createdContext: CreatedContext;
  customerLevelMovAmount?: string | null;
  customerLevelMovCurrency?: string | null;
}

// Response types
export interface CreatedChatResponse {
  id: string;
  chatName?: string;
  restaurantName?: string;
  type: CreateChatType;
  enabled: boolean;
  createdAt: string;
  buyer?: {
    id: string;
    name: string;
  };
  supplier?: {
    id: string;
    name: string;
  };
}

/**
 * A create-time chat setting applied after `chatCreate` succeeds. Used to
 * report the EXACT setting that failed in a `partial` result so the operator
 * knows precisely what to finish in the already-created chat.
 */
export enum PostCreateSetting {
  PRICE_VISIBILITY = 'price visibility',
  CUSTOM_CATALOG = 'custom catalog',
  DELIVERY_SETTINGS = 'delivery settings',
  PO_NUMBER = 'eCommerce PO number',
  NOTIFICATION_PREFERENCES = 'notification preferences',
}

/**
 * Discriminated result of a chat-creation attempt.
 *
 * This is the create-safety contract. It distinguishes a `chatCreate` failure
 * (`error` — no chat exists, so a retry is safe) from a post-create
 * configuration failure (`partial` — the chat DOES exist, so a blind retry
 * would create a duplicate). The created chat id is always preserved on the
 * `partial` branch so the caller can point the operator at the existing chat
 * instead of inviting a retry, and `failedSetting` names the exact post-create
 * setting that failed (#125 applies catalog, delivery, PO-number and
 * notification settings after create — each reports itself here).
 *
 * The data hook (`useCreateChat`) returns this and performs no user-facing
 * side effects; a single upper layer (`useCreateChatForm`) owns the
 * notifications and navigation that follow.
 */
export type CreateChatResult =
  | { status: 'success'; chat: CreatedChatResponse }
  | {
      status: 'partial';
      chat: CreatedChatResponse;
      failedSetting: PostCreateSetting;
      postCreateError: Error;
    }
  | { status: 'error'; error: Error };

// Hook configuration types
export interface CreateChatConfig {
  /**
   * Side-channel exception reporter (e.g. Sentry). Invoked for a `chatCreate`
   * failure and for a post-create configuration failure. This is NOT a
   * user-facing notification hook: notifications and navigation are owned by
   * the form layer so a single layer stays in charge of them.
   */
  captureException?: (error: Error) => void;
}

// Search and validation types
export interface ChatValidationRules {
  customerNumberMandatory: boolean;
  requiresBuyerUsers: boolean;
  requiresSupplierUsers: boolean;
  minDeliveryDays: number;
}

export interface CreateChatSearchParams {
  buyerSearchTerm?: string;
  supplierSearchTerm?: string;
  userSearchTerm?: string;
  isPuppy?: boolean;
}

// State management types
export interface CreateChatState {
  currentStep: number;
  isLoading: boolean;
  error: string | null;
  formData: Partial<CreateChatFormData>;
  validationRules: ChatValidationRules;
}

// Navigation and routing types
export interface CreateChatRouteParams {
  duplicateFromChatId?: string;
  buyerId?: string;
  supplierId?: string;
}

export interface CreateChatNavigationOptions {
  redirectTo?: string;
  openInNewTab?: boolean;
  preserveFormData?: boolean;
}
